import { type FormEvent, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Building2, Info, Loader2, Pencil, Plus, Search, X } from 'lucide-react';
import { studies } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

// Punch-list f11 — logos are stored as a base64 data URI directly on the
// sponsor row (no S3 pipeline for this small, infrequent, non-site-scoped
// asset). Capped client-side too so a user gets instant feedback instead
// of a 400 after upload; must match sponsorsUpdate.ts's server-side cap.
const MAX_LOGO_BYTES = 110_000;

function readFileAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Sprint-19 — Sponsors master-data console (platform_admin only).
 *
 * Single source of truth for the sponsor names that the Study Onboarding
 * Wizard's sponsor dropdown reads from. Admins add a new sponsor here and
 * it is immediately available across every tenant. Data lives in
 * `ctms_sponsors` (already seeded from the BE handler at
 * src/handlers/studies/sponsorsList.ts).
 *
 * Layout mirrors DoaResponsibilitiesPage / RegulatoryTagsPage: stats row →
 * search input + inline add → list. Sponsors are append-only today (no
 * delete / deactivate endpoint exists on the BE); a future ticket can
 * wire those in once the data model decides how to handle the historical
 * studies that reference a retired sponsor.
 */
export function SponsorsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [editingSponsorId, setEditingSponsorId] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['adminSponsors', 'list'],
    queryFn: () => studies.listSponsors(),
    staleTime: 30_000,
  });

  const createM = useMutation({
    mutationFn: (name: string) => studies.createSponsor(name),
    onSuccess: (res) => {
      if (res.created) {
        toast.success(`Added sponsor "${res.sponsor.name}"`);
      } else {
        toast.success(`Sponsor "${res.sponsor.name}" already existed (no duplicate created)`);
      }
      setNewName('');
      qc.invalidateQueries({ queryKey: ['adminSponsors', 'list'] });
      // Also invalidate the wizard's separate query key so a wizard open
      // in another tab sees the new sponsor on next focus.
      qc.invalidateQueries({ queryKey: ['studies-sponsors'] });
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Failed to add sponsor'));
    },
  });

  const sponsors = listQ.data?.sponsors ?? [];
  const rows = listQ.data?.rows ?? [];

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(s));
  }, [rows, search]);

  function submitAdd(e: FormEvent) {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (sponsors.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
      toast.error(`Sponsor "${trimmed}" is already in the list`);
      return;
    }
    createM.mutate(trimmed);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="Sponsors"
        subtitle="Master list of sponsors. Adding one here shows it in the study onboarding picker for every tenant."
      />

      {/* Stats row */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="card p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            Total sponsors
          </div>
          <div className="mt-1 text-[24px] font-semibold text-ink-primary">
            {listQ.isLoading ? '—' : sponsors.length}
          </div>
        </div>
      </div>

      {/* Search + inline add */}
      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-surface-border px-4 py-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sponsors…"
              className="input w-full pl-8"
            />
          </div>
          <form onSubmit={submitAdd} className="flex items-center gap-2 sm:ml-auto">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New sponsor name"
              maxLength={200}
              disabled={createM.isPending}
              className="input w-56"
            />
            <button
              type="submit"
              className="btn-primary shrink-0 whitespace-nowrap"
              disabled={createM.isPending || newName.trim().length === 0}
            >
              {createM.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add sponsor
            </button>
          </form>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sponsors…
          </div>
        ) : listQ.isError ? (
          <div className={`flex items-start gap-2 m-4 rounded border p-4 text-sm ${errorToneClass(listQ.error)}`}>
            {isPermissionError(listQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>
              {errorText(listQ.error, 'Failed to load sponsors.')}
            </span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-ink-muted">
            {search.trim()
              ? `No sponsors matching "${search.trim()}".`
              : 'No sponsors yet. Add the first one above.'}
          </div>
        ) : (
          <ul className="divide-y divide-surface-border">
            {filtered.map((r) => (
              <li
                key={r.sponsor_id}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-muted/40"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="h-3.5 w-3.5 text-ink-muted" />
                  <span className="text-sm text-ink-primary">{r.name}</span>
                </div>
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => setEditingSponsorId(r.sponsor_id)}
                >
                  <Pencil className="h-3.5 w-3.5" /> Company detail
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editingSponsorId && (
        <SponsorDetailDialog
          sponsorId={editingSponsorId}
          sponsorName={rows.find((r) => r.sponsor_id === editingSponsorId)?.name ?? ''}
          onClose={() => setEditingSponsorId(null)}
        />
      )}
    </div>
  );
}

function SponsorDetailDialog({
  sponsorId,
  sponsorName,
  onClose,
}: {
  sponsorId: string;
  sponsorName: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const detailQ = useQuery({
    queryKey: ['adminSponsors', 'detail', sponsorName],
    queryFn: () => studies.sponsorDetail(sponsorName),
    enabled: !!sponsorName,
  });

  const [draft, setDraft] = useState<{
    address: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    website: string;
    logo_data_uri: string | null;
  } | null>(null);

  // Seed the draft once detail loads — a plain if-not-set check (not a
  // useEffect) since this dialog is mounted fresh per sponsor (key'd by
  // the parent conditionally rendering it), so there's no stale-data risk.
  if (detailQ.data && !draft) {
    const d = detailQ.data.sponsor;
    setDraft({
      address: d.address ?? '',
      contact_name: d.contact_name ?? '',
      contact_email: d.contact_email ?? '',
      contact_phone: d.contact_phone ?? '',
      website: d.website ?? '',
      // `||`, not `??`: Caspio returns '' for a sponsor with no logo, and
      // sending '' back failed the BE's data-URI check on every save.
      logo_data_uri: d.logo_data_uri || null,
    });
  }

  const saveM = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Not loaded yet');
      return studies.updateSponsor(sponsorId, {
        address: draft.address.trim() || null,
        contact_name: draft.contact_name.trim() || null,
        contact_email: draft.contact_email.trim() || null,
        contact_phone: draft.contact_phone.trim() || null,
        website: draft.website.trim() || null,
        logo_data_uri: draft.logo_data_uri,
      });
    },
    onSuccess: () => {
      toast.success('Sponsor detail saved');
      qc.invalidateQueries({ queryKey: ['adminSponsors', 'detail', sponsorName] });
      qc.invalidateQueries({ queryKey: ['study', 'sponsor-detail', sponsorName] });
      onClose();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to save')),
  });

  async function handleLogoPick(file: File) {
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Logo is too large — please use an image under ~100KB.');
      return;
    }
    try {
      const dataUri = await readFileAsDataUri(file);
      setDraft((d) => (d ? { ...d, logo_data_uri: dataUri } : d));
    } catch {
      toast.error('Could not read that file.');
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
      <div className="card w-full max-w-lg space-y-4 p-6">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold">{sponsorName} — company detail</h3>
          <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        {detailQ.isLoading || !draft ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded border border-surface-border bg-surface-muted">
                {draft.logo_data_uri ? (
                  <img src={draft.logo_data_uri} alt="" className="h-full w-full object-contain" />
                ) : (
                  <Building2 className="h-6 w-6 text-ink-muted" />
                )}
              </div>
              <div className="space-y-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleLogoPick(f);
                  }}
                />
                <button type="button" className="btn-secondary text-xs" onClick={() => fileInputRef.current?.click()}>
                  {draft.logo_data_uri ? 'Replace logo' : 'Upload logo'}
                </button>
                {draft.logo_data_uri && (
                  <button
                    type="button"
                    className="ml-2 text-xs text-ink-muted hover:text-danger-700"
                    onClick={() => setDraft((d) => (d ? { ...d, logo_data_uri: null } : d))}
                  >
                    Remove
                  </button>
                )}
                <p className="text-[10px] text-ink-muted">PNG/JPEG/GIF/SVG/WebP, under ~100KB. Optional.</p>
              </div>
            </div>

            <div>
              <label className="label text-xs">Address</label>
              <input className="input" maxLength={500} value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label text-xs">Contact name</label>
                <input className="input" maxLength={200} value={draft.contact_name} onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} />
              </div>
              <div>
                <label className="label text-xs">Contact email</label>
                <input className="input" maxLength={200} value={draft.contact_email} onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })} />
              </div>
              <div>
                <label className="label text-xs">Contact phone</label>
                <input className="input" maxLength={50} value={draft.contact_phone} onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })} />
              </div>
              <div>
                <label className="label text-xs">Website</label>
                <input className="input" maxLength={300} value={draft.website} onChange={(e) => setDraft({ ...draft, website: e.target.value })} placeholder="https://" />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
              <button type="button" className="btn-primary" disabled={saveM.isPending} onClick={() => saveM.mutate()}>
                {saveM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
